/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Upload size bounds for a single photo, in bytes.
 *
 * `convertFileToWebP` encodes the canvas at the source image's native
 * resolution, so the converted file tracks the photo's megapixels rather than
 * any thumbnail size -- a 100MB source lands in the tens of MB. Both the
 * pre-conversion pickers and the post-conversion check use this ceiling, and it
 * mirrors `MAX_IMAGE_UPLOAD_BYTES` in the backend presign
 * (`housien/services/properties_image/_upload.py`), which is what S3 actually
 * enforces. Raising one without the other fails the upload at S3.
 */
export const MAX_IMAGE_UPLOAD_BYTES = 100 * 1024 * 1024;

/** Minimum accepted size, rejecting empty or truncated files. */
export const MIN_IMAGE_UPLOAD_BYTES = 1024;

/**
 * base64ToFile function.
 *
 * @param {*} base64String - Parameter description
 * @param {*} fileName - Parameter description
 *
 * @returns {*} Return value description
 */
export function base64ToFile(base64String: string, fileName: string): File {
  const [header, base64Data] = base64String.split(',');
  const mimeType = header.split(':')[1].split(';')[0]; 

  const binaryData = atob(base64Data);

  const uint8Array = new Uint8Array(binaryData.length);
  for (let i = 0; i < binaryData.length; i++) {
    uint8Array[i] = binaryData.charCodeAt(i);
  }

  const fileExtension = mimeType.split('/')[1];

  const finalFileName = `${fileName}.${fileExtension}`;
  return new File([uint8Array], finalFileName, { type: mimeType });
}

/**
 * generateRandomFilename function.
 *
 * @param {*} extension - Parameter description
 *
 * @returns {*} Return value description
 */
export function generateRandomFilename(extension: string): string {
  const randomString = Math.random().toString(36).substr(2, 4); 
  return `image${randomString}.${extension}`;
}

// Helper function to setup image from reader
/**
 * setupImageFromReader component.
 *
 * @param {*} reader - Parameter description
 * @param {*} image - Parameter description
 *
 * @returns {JSX.Element} React component element
 */
function setupImageFromReader(reader: FileReader, image: HTMLImageElement): void {
  reader.onload = () => {
    image.src = reader.result as string;
  };
}

// Helper function to handle reader error
/**
 * handleReaderError function.
 *
 * @param {*} reader - Parameter description
 * @param {*} reject - Parameter description
 *
 * @returns {*} Return value description
 */
function handleReaderError(reader: FileReader, reject: (reason?: any) => void): void {
  reader.onerror = (error) => {
    reject("Erro ao ler o arquivo: " + error);
  };
}

// Helper function to draw image on canvas
/**
 * drawImageOnCanvas component.
 *
 * @param {*} canvas - Parameter description
 * @param {*} ctx - Parameter description
 * @param {*} image - Parameter description
 *
 * @returns {JSX.Element} React component element
 */
function drawImageOnCanvas(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, image: HTMLImageElement): void {
  canvas.width = image.width;
  canvas.height = image.height;
  ctx.drawImage(image, 0, 0);
}

// Helper function to create WebP file from blob
/**
 * createWebPFile function.
 *
 * @param {*} blob - Parameter description
 *
 * @returns {*} Return value description
 */
function createWebPFile(blob: Blob): File {
  const webpFilename = generateRandomFilename('webp');
  return new File([blob], webpFilename, { type: 'image/webp' });
}

/**
 * convertFileToWebP function.
 *
 * @param {*} file - Parameter description
 *
 * @returns {*} Return value description
 */
export async function convertFileToWebP(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const reader = new FileReader();
    
    setupImageFromReader(reader, image);
    handleReaderError(reader, reject);
    reader.readAsDataURL(file);

    image.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject("Erro ao criar contexto do canvas");
        return;
      }

      drawImageOnCanvas(canvas, ctx, image);

      canvas.toBlob((blob) => {
        if (blob) {
          const webpFile = createWebPFile(blob);
          resolve(webpFile);
        } else {
          reject("Erro ao converter para WebP.");
        }
      }, 'image/webp');
    };
    image.onerror = () => {
      reject("Erro ao carregar a imagem.");
    };
  });
}

/**
 * convertFileToJpeg function.
 *
 * Converts any image file to JPEG format using the Canvas API.
 * JPEG is required for portals that do not support WebP (e.g. Instagram).
 */
export async function convertFileToJpeg(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const reader = new FileReader();

    setupImageFromReader(reader, image);
    handleReaderError(reader, reject);
    reader.readAsDataURL(file);

    image.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject('Erro ao criar contexto do canvas');
        return;
      }

      drawImageOnCanvas(canvas, ctx, image);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            const jpegFilename = generateRandomFilename('jpg');
            resolve(new File([blob], jpegFilename, { type: 'image/jpeg' }));
          } else {
            reject('Erro ao converter para JPEG.');
          }
        },
        'image/jpeg',
        0.92,
      );
    };
    image.onerror = () => {
      reject('Erro ao carregar a imagem.');
    };
  });
}

/**
 * fileToBase64 function.
 *
 * @param {*} file - Parameter description
 *
 * @returns {*} Return value description
 */
export async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}